// AnchorIssuesView — top-level ANCHOR dashboard.
//
// Surfaces every ACA issue ANCHOR is tracking + the dashboard
// counts the spec calls for + a CSV import button + a manual log
// modal + per-row action buttons.
//
// API:
//   GET  /api/anchor/issues-list?bucket=<bucket>
//   POST /api/anchor/import-csv
//   POST /api/anchor/log-issue
//   POST /api/anchor/issue-action

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';
import { useIsDemoMode } from '../demo/DemoDataProvider';

// Demo Mode synthesizer — believable ANCHOR issue queue across the
// bucket filter. Shape matches /api/anchor/issues-list ({ issues: [], counts: {} })
// and each issue matches the fields IssueRow reads (severity / status /
// deadline_days / deadline / client_name / issue_type / carrier /
// plan_name / policy_or_app_id / notes / last_contact_at /
// next_followup_at / contact_attempts).
function demoAnchorIssues(bucket) {
  const ALL = [
    { id: 'd-iss-01', severity: 'high',     status: 'open',            deadline_days: 2,  deadline: isoFuture(2),  client_name: 'Robert Chen',       issue_type: 'income_doc_required',     carrier: 'Ambetter',  plan_name: 'Silver 4500 HSA',       policy_or_app_id: 'BCBS-892133', notes: 'Marketplace flagged income for re-verification. ANCHOR drafted a text + email + portal upload link.', last_contact_at: isoPast(0.5), next_followup_at: isoFuture(1), contact_attempts: 2 },
    { id: 'd-iss-02', severity: 'critical', status: 'open',            deadline_days: 0,  deadline: isoFuture(0),  client_name: 'Maria Lopez',       issue_type: 'effectuation_pending',    carrier: 'Cigna',     plan_name: 'Bronze 5800',           policy_or_app_id: 'CIG-440099',  notes: 'Marketplace shows pending; payment due today to lock 2026 effective date.',                       last_contact_at: isoPast(0.2), next_followup_at: isoFuture(0.1), contact_attempts: 4 },
    { id: 'd-iss-03', severity: 'high',     status: 'waiting_client',  deadline_days: 5,  deadline: isoFuture(5),  client_name: 'Sarah Johnson',     issue_type: 'aor_transfer',            carrier: 'Oscar',     plan_name: 'Silver 3000',           policy_or_app_id: 'OSC-211044',  notes: 'Client confirmed switch; ANCHOR sent AOR form via DocuSign. Awaiting signature.',                last_contact_at: isoPast(1),   next_followup_at: isoFuture(2),   contact_attempts: 3 },
    { id: 'd-iss-04', severity: 'normal',   status: 'open',            deadline_days: 9,  deadline: isoFuture(9),  client_name: 'Helen Park',        issue_type: 'missing_coverage_cross_sell', carrier: 'Sun Life', plan_name: 'Dental Premier',     policy_or_app_id: null,           notes: 'Cross-sell: no dental on file. Annual benefit $1,500. Auto-text scheduled tomorrow.',             last_contact_at: null,         next_followup_at: isoFuture(1),   contact_attempts: 0 },
    { id: 'd-iss-05', severity: 'high',     status: 'open',            deadline_days: 3,  deadline: isoFuture(3),  client_name: 'David Cherryholmes',issue_type: 'payment_failure',         carrier: 'Mutual of Omaha', plan_name: 'Final Expense 15K', policy_or_app_id: 'MOO-771432', notes: 'Second NSF in 30 days. RETAIN paused outreach — operator review.',                                last_contact_at: isoPast(0.3), next_followup_at: isoFuture(0.5), contact_attempts: 3 },
    { id: 'd-iss-06', severity: 'normal',   status: 'solved',          deadline_days: null, deadline: null,        client_name: 'Tom Wilson',        issue_type: 'opt_in_capture',          carrier: null,       plan_name: null,                    policy_or_app_id: null,           notes: 'Opt-in confirmed via SMS reply YES. Lead unlocked for nurture.',                                    last_contact_at: isoPast(0.7), next_followup_at: null,            contact_attempts: 1 },
  ];
  let issues = ALL;
  // Bucket filter — keys match BUCKETS in AnchorIssuesView.jsx:67
  // (open / deadlines_72h / needs_human / solved / all). The live
  // BUCKETS tabs use these EXACT keys; using 'waiting_client' or
  // 'cross_sell' here (the old names) silently fell through and
  // returned every issue regardless of tab click.
  if (bucket === 'deadlines_72h')     issues = ALL.filter(i => i.deadline_days != null && i.deadline_days <= 3 && i.status === 'open');
  else if (bucket === 'needs_human')  issues = ALL.filter(i => i.severity === 'critical' || i.status === 'needs_human');
  else if (bucket === 'solved')       issues = ALL.filter(i => i.status === 'solved');
  else if (bucket === 'open')         issues = ALL.filter(i => i.status === 'open');
  // 'all' (and any unrecognized) falls through to ALL.

  // counts shape — every field CountsStrip (line 116) renders.
  // Missing fields display as 'undefined' / NaN in the tiles
  // (not a crash, but ugly). Recompute against ALL so each tile
  // shows the lifetime number, not the filtered slice.
  return {
    bucket,
    issues,
    counts: {
      open:                          ALL.filter(i => i.status === 'open').length,
      deadlines_72h:                 ALL.filter(i => i.deadline_days != null && i.deadline_days <= 3 && i.status === 'open').length,
      aor:                           ALL.filter(i => i.issue_type === 'aor_transfer').length,
      docs_required:                 ALL.filter(i => /doc/i.test(i.issue_type)).length,
      payment_issues:                ALL.filter(i => i.issue_type === 'payment_failure').length,
      plan_expiring:                 ALL.filter(i => /effectuation|plan_expiring|plan_exp/.test(i.issue_type)).length,
      needs_human:                   ALL.filter(i => i.severity === 'critical' || i.status === 'needs_human').length,
      solved_30d:                    ALL.filter(i => i.status === 'solved').length,
      crosssell_opportunities:       ALL.filter(i => i.issue_type === 'missing_coverage_cross_sell').length,
      est_revenue_opportunity_cents: 250_000, // $2,500/yr from the dental cross-sell on Helen Park
    },
    is_demo: true,
  };
}

function isoFuture(days) { return new Date(Date.now() + days * 24 * 3600_000).toISOString(); }
function isoPast(days)   { return new Date(Date.now() - days * 24 * 3600_000).toISOString(); }

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const BUCKETS = [
  { key: 'open',          label: 'Open',         color: 'violet' },
  { key: 'deadlines_72h', label: 'Deadlines <72h', color: 'red' },
  { key: 'needs_human',   label: 'Needs you',    color: 'amber' },
  { key: 'solved',        label: 'Solved (30d)', color: 'emerald' },
  { key: 'all',           label: 'All',          color: 'slate' },
];

const ISSUE_TYPE_LABELS = {
  aor: 'AOR', plan_expiring: 'Plan expiring',
  docs_required: 'Docs', income_docs: 'Income docs',
  identity_docs: 'Identity docs', immigration_docs: 'Immigration docs',
  payment_due: 'Payment due', payment_failed: 'Payment failed',
  policy_inactive: 'Inactive', renewal_needed: 'Renewal',
  consent_needed: 'Consent', marketplace_notice: 'Notice',
  application_review: 'Review',
};

const STATUS_PILL = {
  open:                { bg: 'bg-violet-100', fg: 'text-violet-800', label: 'Open' },
  not_solved:          { bg: 'bg-violet-100', fg: 'text-violet-800', label: 'Working' },
  waiting_client:      { bg: 'bg-amber-100',  fg: 'text-amber-800',  label: 'Waiting on client' },
  waiting_carrier:    { bg: 'bg-sky-100',     fg: 'text-sky-800',    label: 'Waiting on carrier' },
  needs_human:        { bg: 'bg-red-100',     fg: 'text-red-800',    label: 'Needs you' },
  solved:             { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'Solved' },
  client_unresponsive: { bg: 'bg-slate-100',  fg: 'text-slate-700',  label: 'Unresponsive' },
};

const SEVERITY_DOT = {
  critical: 'bg-red-500',
  high:     'bg-amber-500',
  normal:   'bg-sky-400',
  low:      'bg-slate-300',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function fmtCurrency(cents) {
  return `$${Math.round((cents || 0) / 100).toLocaleString()}`;
}

// ── Sub-component: Counts row ────────────────────────────────
function CountsStrip({ counts }) {
  if (!counts) return null;
  const tiles = [
    { label: 'Open issues',           value: counts.open,           color: 'violet'  },
    { label: 'Deadlines < 72h',       value: counts.deadlines_72h,  color: 'red'     },
    { label: 'AOR transfers',         value: counts.aor,            color: 'sky'     },
    { label: 'Docs required',         value: counts.docs_required,  color: 'sky'     },
    { label: 'Payment issues',        value: counts.payment_issues, color: 'red'     },
    { label: 'Plan expiring',         value: counts.plan_expiring,  color: 'amber'   },
    { label: 'Needs you',             value: counts.needs_human,    color: 'amber'   },
    { label: 'Solved (30d)',          value: counts.solved_30d,     color: 'emerald' },
    { label: 'Cross-sell gaps',       value: counts.crosssell_opportunities, color: 'emerald' },
    { label: 'Est. cross-sell value', value: fmtCurrency(counts.est_revenue_opportunity_cents) + '/yr', color: 'emerald', wide: true },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
      {tiles.map(t => (
        <div key={t.label} className={`bg-white border border-slate-200 rounded-lg p-3 ${t.wide ? 'sm:col-span-2 md:col-span-2' : ''}`}>
          <div className="text-xs text-slate-500">{t.label}</div>
          <div className={`text-2xl font-bold ${
            t.color === 'red'     ? 'text-red-700'     :
            t.color === 'violet'  ? 'text-violet-700'  :
            t.color === 'amber'   ? 'text-amber-700'   :
            t.color === 'emerald' ? 'text-emerald-700' :
            t.color === 'sky'     ? 'text-sky-700'     : 'text-slate-700'
          }`}>{t.value || 0}</div>
        </div>
      ))}
    </div>
  );
}

// ── Sub-component: CSV import modal ──────────────────────────
function CsvImportModal({ onClose, onImported, showToast }) {
  const [csv, setCsv] = React.useState('');
  const [batchLabel, setBatchLabel] = React.useState('HealthSherpa ' + new Date().toLocaleDateString());
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const fileRef = React.useRef(null);

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.readAsText(f);
  }

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const body = await apiFetch('/api/anchor/import-csv', {
        method: 'POST',
        body: JSON.stringify({ csv, source_ref: batchLabel || null }),
      });
      setResult(body);
      showToast(`Imported ${body.created} new + ${body.reopened} reopened (${body.skipped.length} skipped)`);
      onImported();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Import HealthSherpa CSV</h3>
          <button aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          {!result && (
            <>
              <p className="text-sm text-slate-600 mb-3">
                Export from HealthSherpa → upload here. We're tolerant of header variations and dedupe against existing issues.
              </p>
              <label className="block text-sm font-medium text-slate-700 mb-1">Batch label</label>
              <input
                type="text"
                value={batchLabel}
                onChange={e => setBatchLabel(e.target.value)}
                className="w-full mb-3 px-3 py-1.5 border border-slate-300 rounded text-sm"
              />
              <label className="block text-sm font-medium text-slate-700 mb-1">Pick a file…</label>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="mb-3 text-sm" />
              <label className="block text-sm font-medium text-slate-700 mb-1">…or paste CSV directly</label>
              <textarea
                value={csv}
                onChange={e => setCsv(e.target.value)}
                rows={8}
                placeholder="Client Name,Phone,Carrier,Plan,Application ID,Issue,Deadline,Notes&#10;John Smith,(512) 555-1234,Aetna,Silver PPO,APP123,Income docs required,12/31/2026,High earner"
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono"
              />
              {err && <div className="mt-2 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-800">{err}</div>}
            </>
          )}
          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{result.created}</div>
                  <div className="text-xs text-emerald-800">Created</div>
                </div>
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-sky-700">{result.reopened}</div>
                  <div className="text-xs text-sky-800">Reopened</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">{result.noop + result.skipped.length}</div>
                  <div className="text-xs text-slate-600">Skipped/dup</div>
                </div>
              </div>
              {result.skipped.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600">Show {result.skipped.length} skipped rows</summary>
                  <div className="mt-2 max-h-40 overflow-y-auto bg-slate-50 border border-slate-200 rounded p-2">
                    {result.skipped.map((s, i) => (
                      <div key={i}><strong>Row {s.row}:</strong> {s.error}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {!result ? (
            <>
              <button aria-label="Close" onClick={onClose} className="px-4 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button onClick={submit} disabled={!csv.trim() || busy} className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-semibold text-sm px-4 py-1.5 rounded">
                {busy ? 'Importing…' : 'Import'}
              </button>
            </>
          ) : (
            <button aria-label="Close" onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-4 py-1.5 rounded">Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-component: IssueRow ──────────────────────────────────
function IssueRow({ issue, onAction, busy }) {
  const status = STATUS_PILL[issue.status] || STATUS_PILL.open;
  const deadlineDays = issue.deadline_days;
  const isUrgent = deadlineDays != null && deadlineDays >= 0 && deadlineDays <= 3;

  return (
    <div className={`bg-white border rounded-lg p-3 ${isUrgent ? 'border-red-300' : 'border-slate-200'}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`w-2 h-2 rounded-full mt-2 ${SEVERITY_DOT[issue.severity] || 'bg-slate-300'}`} title={`severity: ${issue.severity}`}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 truncate">{issue.client_name}</span>
            <span className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-medium">
              {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${status.bg} ${status.fg}`}>
              {status.label}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {issue.carrier || 'No carrier'} · {issue.plan_name || 'No plan'} · {issue.policy_or_app_id || 'no ID'}
            {issue.deadline && (
              <> · deadline {fmtDate(issue.deadline)}
                {deadlineDays != null && (
                  <span className={`ml-1 font-bold ${
                    deadlineDays < 0 ? 'text-red-700'
                    : deadlineDays <= 3 ? 'text-red-700'
                    : deadlineDays <= 7 ? 'text-amber-700'
                    : 'text-slate-500'
                  }`}>({deadlineDays < 0 ? `${Math.abs(deadlineDays)}d past` : `${deadlineDays}d left`})</span>
                )}
              </>
            )}
          </div>
          {issue.notes && <div className="text-xs text-slate-600 mt-1 italic">{issue.notes}</div>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {issue.status !== 'solved' && issue.status !== 'client_unresponsive' && (
            <>
              <button onClick={() => onAction('solve', issue.id, 'Resolved with client')} disabled={busy} className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                ✓ Solve
              </button>
              <button onClick={() => onAction('waiting_client', issue.id)} disabled={busy} className="text-xs px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50">
                Waiting client
              </button>
              <button onClick={() => onAction('escalate', issue.id)} disabled={busy} className="text-xs px-2 py-1 rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                Escalate
              </button>
              <button onClick={() => onAction('run_now', issue.id)} disabled={busy} className="text-xs px-2 py-1 rounded bg-violet-50 border border-violet-300 text-violet-800 hover:bg-violet-100 disabled:opacity-50">
                Send touch now
              </button>
            </>
          )}
          {(issue.status === 'solved' || issue.status === 'client_unresponsive') && (
            <button onClick={() => onAction('reopen', issue.id)} disabled={busy} className="text-xs px-2 py-1 rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Reopen
            </button>
          )}
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-3">
        {issue.last_contact_at && <span>Last contact: {fmtDate(issue.last_contact_at)}</span>}
        {issue.next_followup_at && issue.status !== 'solved' && <span>Next: {fmtDate(issue.next_followup_at)}</span>}
        <span>Attempts: {issue.contact_attempts || 0}</span>
      </div>
    </div>
  );
}

// ── Main exported view ───────────────────────────────────────
export default function AnchorIssuesView({ currentUser, showToast = () => {} }) {
  const isDemo = useIsDemoMode();
  const [bucket, setBucket] = React.useState('open');
  const [data, setData]     = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]   = React.useState(null);
  const [busy, setBusy]     = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!currentUser?.id) return;
    if (isDemo) {
      setData(demoAnchorIssues(bucket));
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true); setError(null);
    try {
      const body = await apiFetch(`/api/anchor/issues-list?bucket=${encodeURIComponent(bucket)}`);
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [bucket, currentUser?.id, isDemo]);

  React.useEffect(() => { refresh(); }, [refresh]);

  async function handleAction(action, issueId, defaultNote) {
    if (!action || !issueId) return;
    let note = defaultNote || null;
    if (action === 'escalate' || action === 'mark_unresponsive') {
      note = window.prompt(`Reason for ${action} (optional):`) || null;
    }
    setBusy(true);
    try {
      await apiFetch('/api/anchor/issue-action', {
        method: 'POST',
        body: JSON.stringify({ issue_id: issueId, action, note }),
      });
      showToast(`Issue ${action.replace('_', ' ')}d.`);
      await refresh();
    } catch (e) {
      showToast(`Action failed: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <span className="text-3xl">⚓</span> ANCHOR · ACA Retention
          </h1>
          <p className="text-sm text-slate-500">
            Auto-follow-up on HealthSherpa-surfaced issues + missing-coverage cross-sell. Closes issues without you.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setImportOpen(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm px-3 py-1.5 rounded"
          >
            Import CSV
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <CountsStrip counts={data?.counts} />

      <div className="flex items-center gap-2 flex-wrap">
        {BUCKETS.map(b => (
          <button
            key={b.key}
            onClick={() => setBucket(b.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
              bucket === b.key
                ? `bg-${b.color}-600 text-white border-${b.color}-600`
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          <strong>Couldn't load issues:</strong> {error}
          {/relation.*aca_issues.*does not exist/i.test(error) && (
            <div className="mt-1 text-xs">Run migration 101 in Supabase to enable ANCHOR.</div>
          )}
        </div>
      )}

      {data && data.issues.length === 0 && !error && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center text-slate-500">
          <p className="text-lg font-medium">No issues in this bucket.</p>
          <p className="text-sm mt-1">Import a HealthSherpa CSV to seed ANCHOR with your open work.</p>
        </div>
      )}

      <div className="space-y-2">
        {(data?.issues || []).map(issue => (
          <IssueRow
            key={issue.id}
            issue={issue}
            onAction={handleAction}
            busy={busy}
          />
        ))}
      </div>

      {importOpen && (
        <CsvImportModal
          onClose={() => setImportOpen(false)}
          onImported={refresh}
          showToast={showToast}
        />
      )}
    </div>
  );
}
